import React from 'react';
import { clsx } from 'clsx';
import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  useListItem
} from '@floating-ui/react';

import ActionsArrowDownNormal from '@strava/icons/ActionsArrowDownNormalXsmall';

import { Button } from '../../Button/Button';
import {
  PopoverSelectOptions,
  usePopoverSelect
} from './usePopoverSelect.hook';

import styles from '../Popover.module.scss';

/**
 * ============================================
 * ============================================
 * POPOVER CONTEXT
 * ============================================
 * ============================================
 * */

type PopoverSelectContextType = ReturnType<typeof usePopoverSelect> | null;

const PopoverSelectContext =
  React.createContext<PopoverSelectContextType>(null);
PopoverSelectContext.displayName = 'PopoverSelectContext';

const usePopoverSelectContext = () => {
  const context = React.useContext(PopoverSelectContext);

  if (context == null) {
    throw new Error(
      'PopoverSelect components must be wrapped in <PopoverSelect />'
    );
  }

  return context;
};

/**
 * ============================================
 * ============================================
 * POPOVER SELECT (parent component)
 * ============================================
 * ============================================
 * */

export interface PopoverSelectProps extends PopoverSelectOptions {
  children?: React.ReactNode;
  renderTriggerButtonContent?: (
    selectedLabel: string | null
  ) => React.ReactNode;
  triggerButtonClassName?: string;
  triggerButtonStyle?: React.CSSProperties;
  triggerButtonDefaultLabel?: string;
  floatingListMaxHeight?: number;
  style?: React.CSSProperties;
}

export const PopoverSelect = ({
  selectedLabel: selectedLabelProp,
  updateSelectedLabel: updateSelectedLabelProp,
  children,
  renderTriggerButtonContent,
  triggerButtonClassName,
  triggerButtonStyle,
  triggerButtonDefaultLabel,
  floatingListMaxHeight = 640,
  style
}: PopoverSelectProps) => {
  const popoverSelectContext = usePopoverSelect({
    selectedLabel: selectedLabelProp,
    updateSelectedLabel: updateSelectedLabelProp,
    maxHeight: floatingListMaxHeight
  });

  // For controlled PopoverSelect, set selected option when popover opens
  React.useEffect(() => {
    if (selectedLabelProp) {
      // Wait for popover animation to finish before setting selected option
      const timeoutId = setTimeout(() => {
        const index =
          popoverSelectContext.labelsRef.current.indexOf(selectedLabelProp);
        popoverSelectContext.setSelectedIndex(index);
        popoverSelectContext.setActiveIndex(index);
        popoverSelectContext.setSelectedLabel(selectedLabelProp);
      }, 150);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [selectedLabelProp, popoverSelectContext.isOpen]);

  return (
    <PopoverSelectContext.Provider value={popoverSelectContext}>
      <TriggerButton
        className={triggerButtonClassName}
        triggerButtonDefaultLabel={triggerButtonDefaultLabel}
        style={triggerButtonStyle}
      >
        {renderTriggerButtonContent?.(popoverSelectContext.selectedLabel) ||
          null}
      </TriggerButton>
      {popoverSelectContext.isOpen && (
        <FloatingPortal>
          <FloatingFocusManager
            context={popoverSelectContext.context}
            modal={false}
          >
            <div
              ref={popoverSelectContext.refs.setFloating}
              style={{
                ...popoverSelectContext.floatingStyles,
                zIndex: 111,
                ...style
              }}
              {...popoverSelectContext.getFloatingProps()}
              className={clsx(styles.popover, styles.popoverSelectContent)}
            >
              <div className={styles.popoverFloatingListWrapper}>
                <FloatingList
                  elementsRef={popoverSelectContext.elementsRef}
                  labelsRef={popoverSelectContext.labelsRef}
                >
                  {children}
                </FloatingList>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </PopoverSelectContext.Provider>
  );
};

/**
 * ============================================
 * ============================================
 * POPOVER TRIGGER BUTTON
 * ============================================
 * ============================================
 * */

interface TriggerButtonProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  triggerButtonDefaultLabel?: string;
}

const TriggerButton = ({
  children,
  className,
  style,
  triggerButtonDefaultLabel = 'Select...'
}: TriggerButtonProps) => {
  const { getReferenceProps, selectedLabel, refs, isOpen } =
    usePopoverSelectContext();

  return (
    <Button
      className={clsx(styles.popoverSelectTriggerButton, className)}
      ref={refs.setReference}
      tabIndex={0}
      data-state={isOpen ? 'open' : 'closed'}
      style={style}
      {...getReferenceProps()}
    >
      {children ? (
        children
      ) : (
        <>
          <span>{selectedLabel || triggerButtonDefaultLabel}</span>
          <ActionsArrowDownNormal
            className={styles.buttonArrowIcon}
            aria-hidden="true"
          />
        </>
      )}
    </Button>
  );
};

/**
 * ============================================
 * ============================================
 * POPOVER OPTION (compound component)
 * ============================================
 * ============================================
 * */

export interface PopoverOptionProps {
  label: string;
  children?: React.ReactNode;
  className?: string;
}

const PopoverOption = ({ label, children, className }: PopoverOptionProps) => {
  const { activeIndex, selectedIndex, getItemProps, handleSelect } =
    usePopoverSelectContext();

  const { ref, index } = useListItem({ label });

  const isActive = activeIndex === index;
  const isSelected = selectedIndex === index;

  return (
    <div
      ref={ref}
      role="option"
      aria-selected={isActive && isSelected}
      tabIndex={isActive ? 0 : -1}
      className={clsx(
        styles.option,
        className,
        isSelected && styles.isSelected
      )}
      {...getItemProps({
        onClick: () => handleSelect(index),
        onKeyDown(event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleSelect(index);
          }
        }
      })}
    >
      {children ?? label}
    </div>
  );
};

PopoverSelect.Option = PopoverOption;
